import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ForgotPasswordSchema } from "schema"
import { z } from "zod"

type FieldValues = z.infer<typeof ForgotPasswordSchema>

type Props = {
  onSubmit: SubmitHandler<FieldValues>
  isPending: boolean
}

export function ForgotPasswordForm({ onSubmit, isPending }: Props) {
  const { t } = useTranslation()
  const form = useForm<FieldValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  })

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={form.handleSubmit(onSubmit)}
      className="w-full"
    >
      <div className="flex flex-col gap-4 mb-4 items-center text-xl">
        <CardTitle>{t("auth.forgotPassword.title")}</CardTitle>
        <CardDescription className="text-foreground">
          {t("auth.forgotPassword.description")}
        </CardDescription>
      </div>
      <div className="gap-4 flex flex-col pt-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("auth.corporateEmail.field")}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t("auth.corporateEmail.placeholder")}
                  autoComplete="new-email"
                  className="bg-background/75"
                  {...field}
                />
              </FormControl>
              <FormDescription />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="flex flex-col gap-4 pt-4">
        <Button disabled={isPending || !form.formState.isValid}>
          {t("auth.forgotPassword.submit")}
        </Button>
      </div>
    </Form>
  )
}
